// Pure logic for the "@"-mention content assist in the Describe box (TopicWizard.tsx).
// No DOM/React here — this stays unit-testable without caret/layout measurement.
import { CatalogueAgent, Role } from '../../lib/a2aApi';

export const ROLE_SUGGESTIONS: Role[] = ['maker', 'checker', 'worker'];

export type MentionKind = 'agent' | 'field' | 'tool' | 'role';
export interface MentionItem { kind: MentionKind; token: string; label: string; sublabel?: string }

/**
 * Builds the flat list of everything "@" can resolve to. Scales to a few dozen/hundred
 * items via a plain filter (see TopicWizard.tsx's useMemo) — if an org's agent or tool
 * catalogue grows much larger, pre-filter `agents`/`tools` before calling this rather
 * than filtering the mapped MentionItem[] downstream.
 */
export function buildMentionVocab(
  agents: CatalogueAgent[],
  fieldClasses: string[],
  tools: string[],
  roles: readonly string[] = ROLE_SUGGESTIONS,
): MentionItem[] {
  return [
    ...agents.map((a): MentionItem => ({ kind: 'agent', token: a.name, label: a.name, sublabel: a.agent_id })),
    ...fieldClasses.map((c): MentionItem => ({ kind: 'field', token: c, label: c })),
    ...tools.map((t): MentionItem => ({ kind: 'tool', token: t, label: t })),
    ...roles.map((r): MentionItem => ({ kind: 'role', token: r, label: r })),
  ];
}

export interface MentionMatch { start: number; query: string }

/**
 * Is the caret inside an active "@query" run? Scans left from the caret for the
 * nearest unescaped "@" on the current line: stops (no match) at whitespace/newline
 * before finding one, and requires the "@" itself to be at the start of the text or
 * preceded by whitespace — so "user@example.com" never triggers the popup.
 */
export function matchMentionQuery(text: string, caret: number): MentionMatch | null {
  if (caret < 0 || caret > text.length) return null;
  let i = caret - 1;
  while (i >= 0) {
    const ch = text[i];
    if (ch === '@') {
      const before = text[i - 1];
      if (i === 0 || before === ' ' || before === '\n' || before === '\t') {
        return { start: i, query: text.slice(i + 1, caret) };
      }
      return null;
    }
    if (ch === ' ' || ch === '\n' || ch === '\t') return null;
    i--;
  }
  return null;
}
